import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bot, Brain, TrendingUp, Zap, Package, Truck, Users, Search as SearchIcon,
  Share2, Target, Megaphone, Globe, FileText, MessageSquare, ChevronDown,
  ChevronRight, Activity, DollarSign, Clock, Shield, Play, Pause,
  AlertTriangle, CheckCircle, BarChart3, GitBranch
} from 'lucide-react';
import { authService } from '../services/auth';
import apiClient from '../services/api';
import { toast } from 'sonner';

const AGENT_ICONS = {
  goal_manager: Target, cmo: Megaphone, pricing: TrendingUp, deal: Zap,
  inventory: Package, fulfillment: Truck, cx: Users, seo: SearchIcon,
  distribution: Share2, reddit: MessageSquare, hackernews: FileText,
  content_writer: FileText, geo_targeting: Globe,
};

const ROLE_COLORS = {
  executive: '#FFD700', manager: '#7A5CFF', worker: '#3EF1C7',
};

const STATUS_COLORS = {
  active: '#3EF1C7', paused: '#FFC857', disabled: '#666', error: '#F55454',
};

export default function AgentOrgChartPage() {
  const navigate = useNavigate();
  const [orgChart, setOrgChart] = useState(null);
  const [budgets, setBudgets] = useState([]);
  const [performance, setPerformance] = useState([]);
  const [expandedAgent, setExpandedAgent] = useState(null);
  const [contextGraph, setContextGraph] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('hierarchy');

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [chartRes, budgetRes, perfRes, projRes] = await Promise.all([
        apiClient.get('/api/v1/agents/org-chart').catch(() => null),
        apiClient.get('/api/v1/agents/budgets').catch(() => null),
        apiClient.get('/api/v1/agents/context/performance/all').catch(() => null),
        apiClient.get('/api/v1/goals/projects').catch(() => null),
      ]);
      setOrgChart(chartRes?.data || getMockOrgChart());
      setBudgets(budgetRes?.data?.budgets || getMockBudgets());
      setPerformance(perfRes?.data?.agents || []);
      setProjects(projRes?.data?.projects || []);
    } catch {
      setOrgChart(getMockOrgChart());
      setBudgets(getMockBudgets());
    } finally {
      setLoading(false);
    }
  };

  const loadAgentContext = async (agentType) => {
    try {
      const res = await apiClient.get(`/api/v1/agents/context/${agentType}/timeline?hours=24`);
      setContextGraph(res.data);
    } catch {
      setContextGraph({ nodes: [], edges: [], summary: { total_nodes: 0, by_type: {} } });
    }
  };

  const toggleExpand = (agentType) => {
    if (expandedAgent === agentType) {
      setExpandedAgent(null);
      setContextGraph(null);
    } else {
      setExpandedAgent(agentType);
      loadAgentContext(agentType);
    }
  };

  // Build tree from flat nodes
  const buildTree = (nodes) => {
    const topLevel = nodes.filter(n => !n.reports_to);
    const getChildren = (parentType) => nodes.filter(n => n.reports_to === parentType);
    return { topLevel, getChildren };
  };

  const getBudget = (agentType) => budgets.find(b => b.agent_type === agentType) || {};

  const renderAgentNode = (node, depth = 0) => {
    const Icon = AGENT_ICONS[node.agent_type] || Bot;
    const statusColor = STATUS_COLORS[node.status] || '#666';
    const roleColor = ROLE_COLORS[node.role_tier] || '#3EF1C7';
    const budget = getBudget(node.agent_type);
    const isExpanded = expandedAgent === node.agent_type;
    const children = orgChart?.nodes?.filter(n => n.reports_to === node.agent_type) || [];

    return (
      <div key={node.agent_type} style={{ marginLeft: depth * 32 }}>
        <div
          onClick={() => toggleExpand(node.agent_type)}
          className="flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all hover:bg-[#1A2340]"
          style={{ border: isExpanded ? '1px solid #7A5CFF' : '1px solid #22304A', background: isExpanded ? '#141D33' : '#0E1426' }}
        >
          {/* Connector line */}
          {depth > 0 && (
            <div className="flex items-center" style={{ marginLeft: -16 }}>
              <div style={{ width: 16, height: 1, background: '#22304A' }} />
            </div>
          )}

          {/* Icon + Status */}
          <div className="relative">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `${roleColor}15`, border: `1px solid ${roleColor}40` }}>
              <Icon className="w-5 h-5" style={{ color: roleColor }} />
            </div>
            <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-[#0E1426]" style={{ background: statusColor }} />
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-white font-medium text-sm">{node.display_name || node.agent_type}</span>
              <span className="text-[9px] px-1.5 py-0.5 rounded" style={{ background: `${roleColor}20`, color: roleColor }}>{node.role_tier}</span>
              <span className="text-[9px] px-1.5 py-0.5 rounded bg-[#22304A] text-[#9CB0C7]">{node.trust_tier}</span>
            </div>
            <div className="text-[11px] text-[#6B8199] mt-0.5">{node.approval_mode} mode</div>
          </div>

          {/* Budget bar */}
          <div className="w-24">
            <div className="flex justify-between text-[10px] text-[#6B8199] mb-0.5">
              <span>Budget</span>
              <span>{budget.used_pct || 0}%</span>
            </div>
            <div className="h-1.5 bg-[#1A2340] rounded-full overflow-hidden">
              <div className="h-full rounded-full transition-all" style={{
                width: `${Math.min(budget.used_pct || 0, 100)}%`,
                background: (budget.used_pct || 0) > 80 ? '#F55454' : '#3EF1C7',
              }} />
            </div>
          </div>

          {/* Expand indicator */}
          {isExpanded ? <ChevronDown className="w-4 h-4 text-[#7A5CFF]" /> : <ChevronRight className="w-4 h-4 text-[#6B8199]" />}
        </div>

        {/* Expanded detail panel */}
        {isExpanded && (
          <div className="ml-12 mt-1 mb-2 p-4 rounded-lg border border-[#22304A] bg-[#0B1120]">
            <div className="grid grid-cols-4 gap-3 mb-4">
              <div className="p-2 rounded bg-[#141D33]">
                <div className="text-[10px] text-[#6B8199]">Budget Used</div>
                <div className="text-lg font-bold text-white">{budget.credits_used_this_period || 0}<span className="text-xs text-[#6B8199]">/{budget.monthly_credit_limit || 0}</span></div>
              </div>
              <div className="p-2 rounded bg-[#141D33]">
                <div className="text-[10px] text-[#6B8199]">Cost/Action</div>
                <div className="text-lg font-bold text-[#3EF1C7]">{budget.cost_per_action || 0} <span className="text-xs">credits</span></div>
              </div>
              <div className="p-2 rounded bg-[#141D33]">
                <div className="text-[10px] text-[#6B8199]">Context Nodes</div>
                <div className="text-lg font-bold text-[#7A5CFF]">{contextGraph?.summary?.total_nodes || 0}</div>
              </div>
              <div className="p-2 rounded bg-[#141D33]">
                <div className="text-[10px] text-[#6B8199]">Status</div>
                <div className="text-lg font-bold" style={{ color: statusColor }}>{node.status}</div>
              </div>
            </div>

            {/* Context Graph Timeline */}
            {contextGraph && contextGraph.nodes && contextGraph.nodes.length > 0 && (
              <div>
                <h4 className="text-xs font-medium text-[#9CB0C7] mb-2 flex items-center gap-1">
                  <GitBranch className="w-3 h-3" /> Thinking Context (24h)
                </h4>
                <div className="space-y-1 max-h-40 overflow-y-auto">
                  {contextGraph.nodes.slice(0, 10).map((n, i) => (
                    <div key={i} className="flex items-center gap-2 text-[11px] p-1.5 rounded bg-[#141D33]">
                      <span className="px-1.5 py-0.5 rounded text-[9px]" style={{
                        background: n.node_type === 'decision' ? '#7A5CFF20' : n.node_type === 'action' ? '#3EF1C720' : '#FFC85720',
                        color: n.node_type === 'decision' ? '#7A5CFF' : n.node_type === 'action' ? '#3EF1C7' : '#FFC857',
                      }}>{n.node_type}</span>
                      <span className="text-[#D1DBE8] flex-1 truncate">{n.label}</span>
                      {n.thinking?.confidence && (
                        <span className="text-[#6B8199]">{(n.thinking.confidence * 100).toFixed(0)}%</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Render children */}
        {children.map(child => renderAgentNode(child, depth + 1))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#0B1120' }}>
        <div className="text-[#9CB0C7]">Loading org chart...</div>
      </div>
    );
  }

  const topLevel = orgChart?.nodes?.filter(n => !n.reports_to) || [];

  return (
    <div className="min-h-screen" style={{ background: '#0B1120' }}>
      <header className="border-b border-[#22304A] bg-[#0E1426]/80 backdrop-blur sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Agent Org Chart</h1>
            <p className="text-sm text-[#9CB0C7]">AI team hierarchy, budgets, schedules & context graph</p>
          </div>
          <div className="flex gap-2">
            {['hierarchy', 'performance', 'projects'].map(tab => (
              <button key={tab} onClick={() => setActiveTab(tab)}
                className={`px-3 py-1.5 rounded text-sm ${activeTab === tab ? 'bg-[#7A5CFF] text-white' : 'bg-[#1A2340] text-[#9CB0C7]'}`}>
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-6">
        {/* Summary cards */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          {[
            { label: 'Total Agents', value: orgChart?.nodes?.length || 0, icon: Bot, color: '#7A5CFF' },
            { label: 'Active', value: orgChart?.nodes?.filter(n => n.status === 'active').length || 0, icon: Activity, color: '#3EF1C7' },
            { label: 'Total Budget', value: budgets.reduce((s, b) => s + (b.monthly_credit_limit || 0), 0), icon: DollarSign, color: '#FFC857' },
            { label: 'Projects', value: projects.length, icon: Target, color: '#F06595' },
          ].map((card, i) => (
            <div key={i} className="p-4 rounded-lg border border-[#22304A] bg-[#0E1426]">
              <div className="flex items-center gap-2 mb-1">
                <card.icon className="w-4 h-4" style={{ color: card.color }} />
                <span className="text-xs text-[#6B8199]">{card.label}</span>
              </div>
              <div className="text-2xl font-bold text-white">{card.value.toLocaleString()}</div>
            </div>
          ))}
        </div>

        {activeTab === 'hierarchy' && (
          <div className="space-y-1">
            <h2 className="text-sm font-medium text-[#9CB0C7] mb-3 flex items-center gap-2">
              <GitBranch className="w-4 h-4" /> Reporting Structure
            </h2>
            {topLevel.map(node => renderAgentNode(node, 0))}
          </div>
        )}

        {activeTab === 'performance' && (
          <div>
            <h2 className="text-sm font-medium text-[#9CB0C7] mb-3 flex items-center gap-2">
              <BarChart3 className="w-4 h-4" /> Agent Performance (Latest)
            </h2>
            <div className="grid grid-cols-1 gap-2">
              {(orgChart?.nodes || []).map(node => {
                const perf = performance.find(p => p.agent_type === node.agent_type);
                const metrics = perf?.metrics || {};
                return (
                  <div key={node.agent_type} className="flex items-center gap-4 p-3 rounded-lg border border-[#22304A] bg-[#0E1426]">
                    <div className="w-8 h-8 rounded flex items-center justify-center bg-[#141D33]">
                      {React.createElement(AGENT_ICONS[node.agent_type] || Bot, { className: 'w-4 h-4 text-[#7A5CFF]' })}
                    </div>
                    <span className="text-sm text-white w-32">{node.display_name}</span>
                    <div className="flex gap-6 text-xs flex-1">
                      <span className="text-[#6B8199]">Decisions: <span className="text-white">{metrics.total_decisions || 0}</span></span>
                      <span className="text-[#6B8199]">Success: <span className="text-[#3EF1C7]">{metrics.success_rate || 0}%</span></span>
                      <span className="text-[#6B8199]">Confidence: <span className="text-[#FFC857]">{((metrics.avg_confidence || 0) * 100).toFixed(0)}%</span></span>
                      <span className="text-[#6B8199]">Budget: <span className="text-white">{metrics.budget_used_pct || 0}%</span></span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {activeTab === 'projects' && (
          <div>
            <h2 className="text-sm font-medium text-[#9CB0C7] mb-3 flex items-center gap-2">
              <Target className="w-4 h-4" /> Active Projects
            </h2>
            {projects.length === 0 ? (
              <div className="text-center py-12 text-[#6B8199]">
                No active projects. Create a goal to auto-generate projects.
              </div>
            ) : (
              <div className="space-y-2">
                {projects.map((proj, i) => (
                  <div key={i} className="p-4 rounded-lg border border-[#22304A] bg-[#0E1426]">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-white font-medium">{proj.title}</span>
                      <span className={`text-xs px-2 py-0.5 rounded ${proj.status === 'completed' ? 'bg-[#3EF1C7]/10 text-[#3EF1C7]' : proj.status === 'active' ? 'bg-[#7A5CFF]/10 text-[#7A5CFF]' : 'bg-[#22304A] text-[#9CB0C7]'}`}>
                        {proj.status}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-[#6B8199]">
                      <span>Owner: {proj.owner_agent}</span>
                      <span>Tasks: {proj.tasks_completed || 0}/{proj.tasks_total || 0}</span>
                      <span>Impact: {proj.estimated_impact_pct || 0}%</span>
                    </div>
                    <div className="mt-2 h-1.5 bg-[#1A2340] rounded-full overflow-hidden">
                      <div className="h-full bg-[#7A5CFF] rounded-full" style={{ width: `${proj.progress_pct || 0}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

// Mock data for development
function getMockOrgChart() {
  return {
    root: 'merchant',
    nodes: [
      { agent_type: 'goal_manager', display_name: 'Goal Manager', status: 'active', reports_to: null, role_tier: 'executive', trust_tier: 'trusted', approval_mode: 'notify', children: [] },
      { agent_type: 'cmo', display_name: 'CMO Agent', status: 'active', reports_to: null, role_tier: 'manager', trust_tier: 'supervised', approval_mode: 'notify', children: ['reddit', 'hackernews', 'content_writer', 'geo_targeting', 'distribution'] },
      { agent_type: 'reddit', display_name: 'Reddit Agent', status: 'active', reports_to: 'cmo', role_tier: 'worker', trust_tier: 'probation', approval_mode: 'approve', children: [] },
      { agent_type: 'hackernews', display_name: 'HN Agent', status: 'active', reports_to: 'cmo', role_tier: 'worker', trust_tier: 'probation', approval_mode: 'approve', children: [] },
      { agent_type: 'content_writer', display_name: 'Content Writer', status: 'active', reports_to: 'cmo', role_tier: 'worker', trust_tier: 'supervised', approval_mode: 'notify', children: [] },
      { agent_type: 'geo_targeting', display_name: 'Geo Targeting', status: 'active', reports_to: 'cmo', role_tier: 'worker', trust_tier: 'probation', approval_mode: 'notify', children: [] },
      { agent_type: 'distribution', display_name: 'Distribution', status: 'active', reports_to: 'cmo', role_tier: 'worker', trust_tier: 'supervised', approval_mode: 'notify', children: [] },
      { agent_type: 'pricing', display_name: 'Pricing Agent', status: 'active', reports_to: null, role_tier: 'worker', trust_tier: 'trusted', approval_mode: 'notify', children: [] },
      { agent_type: 'deal', display_name: 'Deal Agent', status: 'active', reports_to: null, role_tier: 'worker', trust_tier: 'supervised', approval_mode: 'notify', children: [] },
      { agent_type: 'inventory', display_name: 'Inventory Agent', status: 'active', reports_to: null, role_tier: 'worker', trust_tier: 'trusted', approval_mode: 'notify', children: [] },
      { agent_type: 'fulfillment', display_name: 'Fulfillment Agent', status: 'active', reports_to: null, role_tier: 'worker', trust_tier: 'supervised', approval_mode: 'notify', children: [] },
      { agent_type: 'cx', display_name: 'CX Agent', status: 'paused', reports_to: null, role_tier: 'worker', trust_tier: 'probation', approval_mode: 'approve', children: [] },
      { agent_type: 'seo', display_name: 'SEO Agent', status: 'active', reports_to: null, role_tier: 'worker', trust_tier: 'supervised', approval_mode: 'notify', children: [] },
    ],
    initialized: true,
  };
}

function getMockBudgets() {
  return [
    { agent_type: 'pricing', monthly_credit_limit: 1000, credits_used_this_period: 425, used_pct: 42.5, cost_per_action: 5, status: 'active' },
    { agent_type: 'cmo', monthly_credit_limit: 2000, credits_used_this_period: 1650, used_pct: 82.5, cost_per_action: 50, status: 'warning' },
    { agent_type: 'deal', monthly_credit_limit: 500, credits_used_this_period: 180, used_pct: 36, cost_per_action: 10, status: 'active' },
    { agent_type: 'inventory', monthly_credit_limit: 300, credits_used_this_period: 72, used_pct: 24, cost_per_action: 3, status: 'active' },
    { agent_type: 'content_writer', monthly_credit_limit: 800, credits_used_this_period: 460, used_pct: 57.5, cost_per_action: 20, status: 'active' },
    { agent_type: 'seo', monthly_credit_limit: 200, credits_used_this_period: 48, used_pct: 24, cost_per_action: 8, status: 'active' },
  ];
}
